/**
 * `OLE2FRAME` × an in-place edit's affine — the ONE truth for "this edit
 * DECLINES this OLE frame", shared by
 * {@link import("./geometryTransforms").transformEntityCoords} (which leaves
 * the declined entity byte-identical) and the SHELL reducer (which reports the
 * refusal to the user as an RU+EN warning-tone audit row).
 *
 * Same two-site shape as {@link import("./tableScaleRefusal").isTableScaleRefused}
 * — the validity predicate lives here in `command-engine` so the transform
 * arm's guard and the audit message can never drift apart.
 *
 * ## Why an OLE frame declines an OBLIQUE map — @codex PR #785 round 13, FINDING C
 *
 * An `OLE2FRAME` is stored as TWO opposite WCS corners and NOTHING else about
 * its placement. The Autodesk DXF reference's OLE2FRAME group-code table is
 * `70` (version), `3` (object description), `10/20/30` (upper-left corner),
 * `11/21/31` (lower-right corner), `71` (OLE object type), `72` (tile-mode
 * descriptor), `73` (draw aspect), `90` (binary byte count), `310` (the OLE
 * compound document) and `1` (the literal `"OLE"` terminator) — the same set
 * this repo's own reader implements (`dxf-io/entityParser.tableOle.ts`'s
 * `OLE2FRAME_GROUP_READERS`). There is NO rotation scalar and NO `210`
 * extrusion: the frame is axis-aligned in WCS BY CONSTRUCTION, and a rectangle
 * turned off the axes is not a value the entity can hold.
 *
 * Before this module, the `OLE Frame` transform arm answered an unrepresentable
 * map with the AXIS-ALIGNED BOX of the four transformed corners. That box is
 * the tightest frame containing the image, but it is not the image: rotating a
 * 20×10 frame by 45° persisted a **21.213×21.213** frame (verified against
 * `31cca20`), i.e. the embedded picture's committed size changed by +6 % in one
 * axis and +112 % in the other, and groups 10/20 and 11/21 were written out
 * with those new numbers by `dxf-io/writer.ts`. Silently storing a DIFFERENT
 * rectangle is worse than doing nothing: the user is not told, and the next
 * save makes it the file's truth.
 *
 * ## What the sources say
 *
 *  - **Autodesk** — "OLE objects cannot be rotated in AutoCAD": *"It is not
 *    possible to rotate an inserted OLE object or perform a rotated plot or
 *    publish operation on OLE data."* AutoCAD DECLINES the operation; it does
 *    not resize the frame to a bounding box.
 *  - **`ezdxf` 1.4.4** (`ezdxf/entities/oleframe.py`) — `OLE2Frame` keeps the
 *    whole `AcDbOle2Frame` subclass as OPAQUE `Tags` and implements NO
 *    `transform()`, so it inherits `DXFGraphic.transform`, whose body is
 *    `raise NotImplementedError()`. `copy()` likewise raises
 *    `CopyNotSupported`. ezdxf refuses EVERY transform of an OLE2FRAME,
 *    translation included.
 *
 * This module is deliberately NARROWER than ezdxf: a map the two stored
 * corners CAN express exactly is still applied, because refusing an exact,
 * lossless edit would be its own fidelity loss (the same reason
 * `isTableScaleRefused` declines only a genuine RESIZE and lets the
 * `|factor| === 1` point reflection through).
 *
 * ## Which maps are exact, and which are refused
 *
 * The edits this app applies are affine, so the question is entirely about the
 * LINEAR part `L = [[a, c], [b, d]]` (`{a, b}` is the image of `(1, 0)`,
 * `{c, d}` the image of `(0, 1)`). An axis-aligned rectangle maps onto an
 * axis-aligned rectangle exactly when `L` sends the coordinate axes to the
 * coordinate axes, i.e. when `L` is DIAGONAL (`b = c = 0` — the axes are kept)
 * or ANTI-DIAGONAL (`a = d = 0` — the axes are swapped). Anything else turns
 * the rectangle into an oblique rectangle or a parallelogram that the stored
 * pair cannot name.
 *
 * The ANTI-DIAGONAL half is not a curiosity: it is every ±90° turn, and also
 * the mirrors about `y = x` and `y = −x`, which exchange the two axes and are
 * therefore every bit as exact as an axis mirror. Testing "is this a rotation
 * by a multiple of 90°" instead would refuse those two reflections for no
 * reason.
 *
 *   | edit                                   | linear part      | verdict   |
 *   |----------------------------------------|------------------|-----------|
 *   | MOVE (translation)                     | identity         | applied   |
 *   | ROTATE by a multiple of 90°            | diag / anti-diag | applied   |
 *   | point reflection (ROTATE 180°)         | `diag(-1, -1)`   | applied   |
 *   | MIRROR about a horizontal / vertical axis | `diag(±1, ∓1)` | applied   |
 *   | MIRROR about `y = x` or `y = −x`       | anti-diagonal    | applied   |
 *   | SCALE (uniform or per-axis)            | `diag(sx, sy)`   | applied   |
 *   | ROTATE by any other angle              | dense            | REFUSED   |
 *   | MIRROR about any OTHER axis            | dense            | REFUSED   |
 *   | any shear                              | dense            | REFUSED   |
 *
 * Every "applied" row maps the four corners onto the four corners of a NEW
 * axis-aligned rectangle, so the arm's four-corner fold reproduces the affine
 * image EXACTLY and nothing is approximated. The refused rows are precisely
 * the ones where the fold would substitute a box for the image.
 *
 * `EXPLODE` reaches the same verdict for the same reason from the other
 * direction: `explodeBlockChildPolylineTableOle.ts` refuses an OLE2FRAME child
 * whose INSERT rotation is not a multiple of 90° (`"ole-frame-oblique"`). That
 * module tests an AUTHORED rotation scalar and this one tests a PROBED affine,
 * so neither can express the other's input; if the two ever need one predicate,
 * the angle form belongs HERE and that module should call it.
 *
 * ## What "refused" means
 *
 * The entity is returned UNCHANGED — not partially transformed, not moved to
 * where its centre would have gone. A half-applied edit is the same class of
 * silent corruption as the box substitution it replaces, and the sources above
 * describe a refusal, not a partial. (AutoCAD's grip-level `OLEFRAME` rotation
 * handle is a DWG-side affordance with no DXF group code to persist into; it is
 * not reachable from a DXF round trip and is not modelled here.)
 *
 * KNOWN CONSEQUENCE, MEASURED — `geometry/create-arraypolar` AND
 * `create-arraypath`. `apps/web/.../state/arrayReducers.ts` builds each clone
 * by composing `transformEntityCoords` calls, and its second step is
 * `rotatePoint(p, centerPoint, angleDeg)` — an OBLIQUE map for every item
 * whose angle is not a multiple of 90°. A refused clone therefore stays where
 * the radial step left it, so a polar array of an OLE frame at a 45° / 60° /
 * 120° increment STACKS those copies on the source instead of ringing them
 * around the centre (verified: a 20×10 frame arrayed about (100, 100) keeps
 * `upperLeft (0, 10)` at 45°, 60° and 120°, and is placed correctly at 90°,
 * 180° and 270°). BEFORE this change those copies were placed, at the wrong
 * SIZE — 21.213 × 21.213 instead of 20 × 10 at 45°.
 *
 * Which of the two is worse is a product decision, not a geometric one, and it
 * is recorded here rather than decided quietly. The third option — apply the
 * map to the frame's CENTRE and keep the authored width, height and axis
 * alignment — would place the array correctly AND never resize the picture,
 * and is what the Autodesk support note describes for an OLE object under a
 * rotate ("the position changes but the direction does not"); it is NOT
 * implemented here because it is a PARTIAL transform, which is precisely what
 * the finding asked to stop doing for the ROTATE command itself. Choosing it
 * would be a one-line change in {@link isAxisAlignedFramePreservingMap}'s
 * caller plus a centre-preserving branch, and `arrayReducers.ts` would need no
 * edit either way.
 *
 * THE VISIBLE HALF. A refusal the user is never told about is the same silent
 * no-op CLAUDE.md ПРИОРИТЕТ #1 forbids, so {@link summarizeOleFrameTransformRefusal}
 * below gives `apps/web/.../state/geometryShellReducer.ts` the per-dispatch
 * counts it needs to raise the RU+EN warning-tone audit row that SCALE ×
 * ACAD_TABLE already raises (`audit.entities_rotated_ole_frames_declined` /
 * `audit.rotate_declined_ole_frames_only` for ROTATE, and the `mirrored` /
 * `mirror_` pair for MIRROR). The ROTATE and MIRROR arms are the only two that
 * need it: `geometry/move-entities` composes a pure TRANSLATION (linear part
 * the identity) and `geometry/scale-entities` a `diag(factor, factor)`, both of
 * which {@link isAxisAlignedFramePreservingMap} accepts for EVERY input the
 * command schema admits, so neither arm can produce a refusal to report —
 * pinned by name in `editorStore.oleFrameTransformRefusal.test.ts` rather than
 * asserted here.
 */

import type { DrawingEntity, DrawingSpace } from "@dxf-ai-editor/drawing-model";

import { probeAffine2d, type TransformFn } from "./affine2dProbe";
import {
  summarizeSelectionRefusal,
  type SelectionRefusalSummary,
} from "./selectionRefusalWalk";

/**
 * Slack on "this coefficient is zero", taken RELATIVE to the map's own scale.
 *
 * A drawing expresses no angle this small — `1e-9` of a unit direction is
 * ~5.7e-8°, the same order as `geometryTransforms.ts`'s own
 * `DIRECTION_NOOP_TOLERANCE` for "did this edit actually turn the table?" and
 * as `explodeBlockChildPolylineTableOle.ts`'s `RIGHT_ANGLE_TOLERANCE_DEG` — so
 * it absorbs the representation error of a `270°` that travelled through a
 * parse without ever admitting a rotation a user could see.
 *
 * RELATIVE rather than absolute because the coefficients carry the edit's
 * SCALE: a `1e-6` uniform shrink composed with an exact 90° turn has
 * `|a| = |d| = 1e-6`, which an absolute `1e-9` floor would misread as the
 * anti-diagonal it genuinely is only by luck, and a `1e6` blow-up composed with
 * a 1e-12-radian turn would have off-diagonal terms of `1e-6` that an absolute
 * test would call oblique.
 */
const AXIS_ALIGNMENT_TOLERANCE = 1e-9;

/**
 * Recover the linear part of the affine `fn` from three probes and answer
 * whether it maps an axis-aligned rectangle onto an axis-aligned rectangle.
 *
 * The recovery itself is {@link probeAffine2d} — the ONE three-probe affine
 * reader this package has, shared with `ellipseEditTransform.ts` and
 * `geometryTransforms.ts`'s `linearPart`, so "what affine is this callback?"
 * is answered in one place.
 *
 * DEGENERATE maps, which no app edit produces (`scalePoint` rejects a zero
 * factor at the command schema; rotations and mirrors are invertible), fall
 * two different ways and both are deliberate. A CONSTANT map — every
 * coefficient zero — passes: its image IS the degenerate rectangle the two
 * equal corners spell out, so nothing is substituted. A RANK-1 collapse onto a
 * line is REFUSED, because its linear part is neither diagonal nor
 * anti-diagonal and refusing writes nothing. Both are pinned by name in
 * `oleFrameTransformRefusal.test.ts`.
 */
export function isAxisAlignedFramePreservingMap(fn: TransformFn): boolean {
  const { a, b, c, d } = probeAffine2d(fn);
  // EACH coefficient, not their SUM (targeted mutation testing, this file's
  // first run): `Number.isFinite(a + b + c + d)` reads as the same check and
  // is not — the sum of four FINITE coefficients can OVERFLOW to `Infinity`
  // (`a = d = 1e308`, an enormous but perfectly representable uniform scale),
  // which would have refused a map the frame can express exactly. It also
  // cancels: `a = 1e308, d = -1e308` sums to a finite `0` from two finite
  // parts, so the sum form is unreliable in both directions.
  if (
    !Number.isFinite(a) ||
    !Number.isFinite(b) ||
    !Number.isFinite(c) ||
    !Number.isFinite(d)
  ) {
    return false;
  }
  const scale = Math.max(Math.abs(a), Math.abs(b), Math.abs(c), Math.abs(d));
  const floor = AXIS_ALIGNMENT_TOLERANCE * scale;
  // DIAGONAL — the axes are kept (identity, SCALE, axis MIRROR, 180° ROTATE).
  if (Math.abs(b) <= floor && Math.abs(c) <= floor) return true;
  // ANTI-DIAGONAL — the axes are swapped (±90° ROTATE, diagonal-axis MIRROR).
  return Math.abs(a) <= floor && Math.abs(d) <= floor;
}

/**
 * `true` when an in-place edit whose affine is `fn` must DECLINE `entity`
 * rather than commit a rectangle the entity never had. See the module header.
 *
 * Total and pure: every non-`OLE Frame` entity transforms normally, and so does
 * an OLE frame under any map its two stored corners can express exactly.
 */
export function isOleFrameTransformRefused(
  entity: DrawingEntity,
  fn: TransformFn,
): boolean {
  return entity.type === "OLE Frame" && !isAxisAlignedFramePreservingMap(fn);
}

/**
 * What an in-place transform whose affine is `fn` will actually do to
 * `entityIds`: which of them are OLE frames this map cannot express (declined,
 * in document order) and how many entities WILL be transformed.
 *
 * The walk itself — id resolution, de-duplication, "an id that matches nothing
 * counts as neither", document order — is
 * {@link import("./selectionRefusalWalk").summarizeSelectionRefusal}, the ONE
 * such walk this package has, shared with the SCALE × `Table` refusal
 * (`tableScaleRefusal.ts`). Only {@link isOleFrameTransformRefused} and the
 * message ids the shell reducer picks are this refusal's own.
 *
 * `fn` is re-probed per candidate entity rather than once up front. The probe
 * is three calls to `fn` and the walk only reaches the predicate for ids that
 * RESOLVED, so the cost is three evaluations per selected entity — nothing
 * beside the transform itself, which evaluates `fn` once per coordinate. The
 * alternative (hoist {@link isAxisAlignedFramePreservingMap} and test
 * `entity.type === "OLE Frame"` inline here) would restate the predicate at a
 * second site, which is the drift this module's two-site shape exists to
 * prevent.
 */
export function summarizeOleFrameTransformRefusal(
  spaces: readonly DrawingSpace[],
  entityIds: readonly string[],
  fn: TransformFn,
): SelectionRefusalSummary {
  return summarizeSelectionRefusal(spaces, entityIds, (entity) =>
    isOleFrameTransformRefused(entity, fn),
  );
}
